import type { ProductNode } from "@/entities/product/api/productService";

/**
 * 多产品带料(2026-08-19 老板:「我们选择产品时…是否可以跟其他地方保持一致和多选」)。
 *
 * ═══ 为什么能零契约改动 ═══════════════════════════════════════════════════════
 * api 侧 `VideoDraftFormUtil` 从 formJson 读的是**三个单值键**:
 *     briefProductId     → brief["productId"]      (String)
 *     briefProductName   → brief["productName"]    (String)
 *     briefSellingPoints → brief["sellingPoints"]  (数组 → JSON 串,ai 侧解回)
 * 其中 `sellingPoints` 本来就是数组、**天然装得下多个产品的卖点**;productName 是字符串,
 * 拼起来就行。所以这里的做法是:**`briefProducts` 数组当真源,那三个旧键做派生投影**
 * (`projectBrief`)—— api 与 ai **一行都不用改**,老草稿也读得回来。
 *
 * ═══ 品牌事实锚定只在单选时给 ═══════════════════════════════════════════════
 * `productId` 在 ai 侧是 `require_approved` 品牌事实的 **strong 作用域锚**。多选时锚哪个
 * 都是猜 —— 「取第一个」正是那种**能跑、但没人说得清为什么**的隐晦行为。所以多选时留空:
 *
 *     选一个 = 深度带料 + 品牌事实锚定
 *     选多个 = 只带卖点
 *
 * 两句话就能对用户讲清楚,也就能写进界面。
 *
 * ═══ 上限 4 ═══════════════════════════════════════════════════════════════
 * 不是拍脑袋:ai 侧 `reference_compiler._ROLE_QUOTAS["product"] = 4` —— 一段最多挂 4 张
 * 产品参考图,再多也进不去画面(总图槽 9 张,产品占 4)。改那边之前先改这里。
 */

/** 与 ai `reference_compiler._ROLE_QUOTAS["product"]` 同源。 */
export const MAX_BRIEF_PRODUCTS = 4;

export interface BriefProduct {
  /** 产品树节点码。 */
  code: string;
  name: string;
  /** 勾着的卖点(上车的就是这些)。 */
  points: string[];
  /** 该品的卖点全集 —— 点掉的想点回来只能靠它(草稿往返后产品树不在手上)。 */
  allPoints: string[];
}

function cleanPoints(node: ProductNode): string[] {
  return (node.sellingPoints ?? []).filter((p) => p && p.trim());
}

/** 选一个品。已选过 → 原样返回(**不重置已点掉的卖点**);满 4 个 → 原样返回。 */
export function addBriefProduct(list: BriefProduct[], node: ProductNode): BriefProduct[] {
  if (list.some((p) => p.code === node.code)) return list;
  if (list.length >= MAX_BRIEF_PRODUCTS) return list;
  const points = cleanPoints(node);
  return [...list, { code: node.code, name: node.name, points: [...points], allPoints: [...points] }];
}

export function removeBriefProduct(list: BriefProduct[], code: string): BriefProduct[] {
  return list.filter((p) => p.code !== code);
}

/** 某个品的某条卖点去/留。顺序按该品的全集,不自排序。 */
export function toggleProductPoint(
  list: BriefProduct[],
  code: string,
  point: string
): BriefProduct[] {
  return list.map((p) => {
    if (p.code !== code) return p;
    const on = new Set(p.points);
    if (on.has(point)) {
      on.delete(point);
    } else if (p.allPoints.includes(point)) {
      on.add(point);
    }
    return { ...p, points: p.allPoints.filter((x) => on.has(x)) };
  });
}

export interface ProjectedBrief {
  briefProductId: string | null;
  briefProductName: string | null;
  briefSellingPoints: string[] | null;
  briefSellingPointsAll: string[] | null;
}

/**
 * 数组 → 出站的三个旧键(外加全集,草稿回读用)。**这是 api 真正读到的东西**。
 *
 * ★`briefProductId` 只在**恰好一个**产品时给 —— 见文件头注:多选时锚谁都是猜。
 */
export function projectBrief(list: BriefProduct[]): ProjectedBrief {
  if (list.length === 0) {
    return {
      briefProductId: null,
      briefProductName: null,
      briefSellingPoints: null,
      briefSellingPointsAll: null,
    };
  }
  return {
    briefProductId: list.length === 1 ? list[0].code : null,
    briefProductName: list.map((p) => p.name).join("、"),
    briefSellingPoints: list.flatMap((p) => p.points),
    briefSellingPointsAll: list.flatMap((p) => p.allPoints),
  };
}

/**
 * 老草稿(只有三个旧键)→ 单元素数组。
 *
 * 2026-08-18 VID-BRIEF-1 之后、本次之前存下的草稿都是这个形状,打开时要能原样回来。
 * 没选过品(`briefProductId` 空)→ 空数组:别造一个空壳产品出来占着 chips 区。
 */
export function briefProductsFromLegacy(form: Record<string, unknown>): BriefProduct[] {
  const code = form.briefProductId;
  if (typeof code !== "string" || !code.trim()) return [];
  const all = Array.isArray(form.briefSellingPointsAll)
    ? (form.briefSellingPointsAll as string[])
    : [];
  const picked = Array.isArray(form.briefSellingPoints)
    ? (form.briefSellingPoints as string[])
    : all;
  return [
    {
      code,
      name: typeof form.briefProductName === "string" ? form.briefProductName : code,
      points: [...picked],
      allPoints: all.length > 0 ? [...all] : [...picked],
    },
  ];
}
